<template>
  <div class="share-calculation" data-share-exclude>
    <button
      class="share-calculation__button"
      type="button"
      :disabled="isCreating"
      :aria-label="isCreating ? 'Создаём снимок расчёта' : 'Поделиться расчётом'"
      title="Поделиться расчётом"
      @click="shareCalculation"
    >
      <svg
        v-if="!isCreating"
        class="share-calculation__icon"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="18" cy="5" r="2.5"/>
        <circle cx="6" cy="12" r="2.5"/>
        <circle cx="18" cy="19" r="2.5"/>
        <path d="m8.3 10.9 7.4-4.7M8.3 13.1l7.4 4.7"/>
      </svg>
      <span v-else class="share-calculation__spinner" aria-hidden="true"></span>
    </button>
    <div v-if="message" class="share-calculation__message" role="status">
      {{ message }}
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import html2canvas from 'html2canvas'

const isCreating = ref(false)
const message = ref('')

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) resolve(blob)
    else reject(new Error('Не удалось создать изображение'))
  }, 'image/png', 1)
})

const downloadFile = (file) => {
  const url = URL.createObjectURL(file)
  const link = document.createElement('a')
  link.href = url
  link.download = file.name
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const shareCalculation = async () => {
  const target = document.querySelector('.main')
  if (!target || isCreating.value) return

  const captureWidth = Math.round(target.getBoundingClientRect().width + 40)

  isCreating.value = true
  message.value = ''

  try {
    const canvas = await html2canvas(target, {
      backgroundColor: null,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      width: captureWidth,
      windowWidth: captureWidth,
      onclone: (clonedDocument) => {
        clonedDocument.querySelectorAll('[data-share-exclude]').forEach((node) => node.remove())

        const clonedTarget = clonedDocument.querySelector('.main')
        if (!clonedTarget) return

        clonedTarget.style.height = 'auto'
        clonedTarget.style.minHeight = '100vh'
        clonedTarget.style.width = `${captureWidth}px`
        clonedTarget.style.boxSizing = 'border-box'
        clonedTarget.style.justifyContent = 'flex-start'

        clonedTarget.querySelectorAll('*').forEach((node) => {
          const style = clonedDocument.defaultView.getComputedStyle(node)
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            node.style.overflow = 'visible'
            node.style.height = 'auto'
            node.style.maxHeight = 'none'
          }
        })
      },
    })

    const blob = await canvasToBlob(canvas)
    const file = new File([blob], `расчёт-${new Date().toISOString().slice(0, 10)}.png`, {
      type: 'image/png',
    })
    const shareData = {
      title: 'Расчёт ювелира',
      text: 'Мой расчёт',
      files: [file],
    }

    if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
      await navigator.share(shareData)
    } else {
      downloadFile(file)
      message.value = 'Изображение сохранено — теперь им можно поделиться.'
    }
  } catch (error) {
    if (error?.name !== 'AbortError') {
      message.value = 'Не удалось создать снимок. Попробуйте ещё раз.'
    }
  } finally {
    isCreating.value = false
  }
}
</script>

<style scoped>
.share-calculation {
  position: fixed;
  right: 14px;
  bottom: calc(50px + env(safe-area-inset-bottom, 0px));
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.share-calculation__button {
  display: grid;
  width: 46px;
  height: 46px;
  padding: 0;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(145deg, rgba(111, 72, 179, 0.96), rgba(41, 5, 95, 0.96));
  box-shadow: 0 5px 16px rgba(25, 0, 66, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.share-calculation__button:active:not(:disabled) {
  transform: scale(0.94);
  box-shadow: 0 3px 10px rgba(25, 0, 66, 0.28);
}

.share-calculation__button:disabled {
  opacity: 0.7;
  cursor: wait;
}

.share-calculation__icon {
  width: 23px;
  height: 23px;
  overflow: visible;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.share-calculation__spinner {
  width: 19px;
  height: 19px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: share-spin 0.75s linear infinite;
}

.share-calculation__message {
  max-width: 260px;
  padding: 8px 12px;
  border-radius: 10px;
  color: #fff;
  background: rgba(25, 0, 66, 0.94);
  font-size: 12px;
  line-height: 1.35;
  text-align: left;
}

@keyframes share-spin {
  to { transform: rotate(360deg); }
}
</style>
